import classNames from 'classnames';
import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import type { Bounds, Parchment } from 'quill/core';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import * as styles from './CursorSelection.module.scss';
import Attachment from './formats/Attachment';
import ButtonBlot from './formats/ButtonBlot';
import { useUIElementResizeListener } from './UIElementResizeContext';
import useEditorWidthChange from './useEditorWidthChange';
import { useNestedTargetForRemoteSelection } from './utils/getNestedTargetForRemoteSelection';
import type Mark from './utils/Mark';
import useAppSelector from '../app/redux/useAppSelector';

function getZIndex(element: HTMLElement) {
  const parsedIndex = parseInt(getComputedStyle(element).zIndex, 10);
  if (Number.isNaN(parsedIndex)) return null;
  return parsedIndex;
}

interface CursorSelectionProps {
  mark: Mark;
  quill: Quill;
  className?: string;
  user?: { index?: number };
}

const CursorSelection = ({
  mark,
  quill,
  className,
  user = {},
}: CursorSelectionProps) => {
  const version = useAppSelector((state) => state.editor.version);
  const [zIndex, setZIndex] = useState<number | undefined>(undefined);
  const [position, setPosition] = useState<{
    embed?: Parchment.EmbedBlot | null;
    boundaries: Bounds[];
    quill: Quill;
  } | null>(null);

  const target = useNestedTargetForRemoteSelection(quill, mark);

  const updatePosition = useCallback(() => {
    if (target) {
      setPosition({
        // @ts-expect-error
        boundaries: target.quill.getClientRects(
          target.range.index,
          target.range.length,
        ),
        quill: target.quill,
        embed: target.embed,
      });
    } else {
      setPosition(null);
    }
  }, [target]);

  useEffect(() => {
    if (!target?.embed) return undefined;

    if (target.embed instanceof ButtonBlot) {
      const resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(target.embed.getReferenceNodeForBounds());

      return () => {
        resizeObserver.disconnect();
      };
    }

    if (target.embed instanceof Attachment) {
      const attachmentArea = target.embed.getAttachmentArea();
      if (!attachmentArea) return undefined;

      const resizeObserver = new ResizeObserver(updatePosition);
      resizeObserver.observe(attachmentArea);

      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target instanceof HTMLElement) {
            const isExpanded = mutation.target.dataset.expanded === 'true';

            if (isExpanded) {
              const currentZIndex = getZIndex(mutation.target);
              if (currentZIndex) {
                setZIndex(currentZIndex + 1);
              }
            } else {
              setZIndex(undefined);
            }
          }
        });
      });

      mutationObserver.observe(attachmentArea, {
        attributeFilter: ['data-expanded', 'class'],
      });

      return () => {
        resizeObserver.disconnect();
        mutationObserver.disconnect();
      };
    }

    // This makes sure that for embeds with z-index like images or carousels
    // the cursor selection is always on top of them
    if (target.embed.domNode instanceof HTMLElement) {
      const currentZIndex = getZIndex(target.embed.domNode);
      if (currentZIndex) {
        setZIndex(currentZIndex + 1);
      }
    }

    return undefined;
  }, [target, updatePosition]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition, version]);

  const addUIElementResizeListener = useUIElementResizeListener();
  useEffect(() => {
    return addUIElementResizeListener(updatePosition);
  }, [updatePosition, addUIElementResizeListener]);

  useEditorWidthChange(position?.quill, updatePosition);

  if (!position || position.boundaries.length <= 0) return null;

  const { embed } = position;

  return createPortal(
    <div className={styles.container}>
      {_.map(position.boundaries, (bounds, i) => {
        return (
          <span
            key={i}
            className={classNames(
              styles.innerContainer,
              user.index == null ? null : styles[`color${user.index + 1}`],
              embed ? styles.embed : null,
              embed ? styles[embed.statics.blotName] : null,
            )}
            style={
              {
                zIndex,
                '--cursor-selection-left': bounds.left,
                '--cursor-selection-height': bounds.height,
                '--cursor-selection-top': bounds.top,
                '--cursor-selection-width': bounds.width,
              } as React.CSSProperties
            }
          >
            <span key={i} className={classNames(styles.selection, className)} />
          </span>
        );
      })}
    </div>,
    position.quill.container,
  );
};

export default CursorSelection;
